// src/components/dashboards/charts/CancerTypeBar.tsx
// Horizontal bar chart showing patient distribution by cancer domain.

import React from "react";
import { Box, Skeleton } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme, { seriesGradient } from "./useChartTheme";
import { athenaColors } from "../../../theme/palette";
import { DomainCount } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: DomainCount[] | undefined;
  loading: boolean;
}

const CancerTypeBar: React.FC<Props> = ({ data, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, seriesColors, textPrimary, textSecondary, chartHeight, dark } =
    useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  if (data.length === 0) return null;

  // Sort ascending so largest bar is at top
  const sorted = [...data].sort((a, b) => a.count - b.count);
  const labels = sorted.map((d) => {
    const parts = d.domain.split(".");
    return parts[parts.length - 1].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  });
  const values = sorted.map((d) => d.count);

  const option = {
    title: {
      text: t("dashboard.analytics.cancer_distribution", "Cancer Type Distribution"),
      subtext: t(
        "dashboard.analytics.cancer_distribution_sub",
        "Number of patients by cancer type",
      ),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: textSecondary, fontSize: 11 },
      left: 0,
    },
    tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { ...baseGrid, left: "25%", right: "12%", bottom: "10%", top: "22%" },
    xAxis: {
      ...baseAxis,
      type: "value",
      name: t("dashboard.analytics.axis_patients", "Number of patients"),
      nameLocation: "middle" as const,
      nameGap: 28,
      nameTextStyle: { color: textSecondary, fontSize: 10 },
    },
    yAxis: {
      ...baseAxis,
      type: "category",
      data: labels,
      axisLabel: { ...baseAxis.axisLabel, width: 100, overflow: "truncate" },
    },
    series: [
      {
        type: "bar",
        data: values,
        itemStyle: {
          color: seriesGradient(
            seriesColors[0],
            "horizontal",
            dark ? athenaColors.royalBlue : undefined,
          ),
          borderRadius: [0, 4, 4, 0],
        },
        barMaxWidth: 28,
        // A cohort's cancer mix is a power law: here Skin is 21,415 of 25,365,
        // 84%, so on a plain linear axis every other category collapses to a
        // sliver and the chart says nothing except "mostly skin".
        //
        // The axis stays LINEAR because the bar lengths must remain honest
        // about magnitude. Instead:
        //   barMinHeight  guarantees a non-zero category is always visible,
        //                 so "small" never renders as "absent";
        //   label         prints the actual count at the end of each bar, so
        //                 the exact value is readable even when the bar is at
        //                 that floor.
        barMinHeight: 3,
        label: {
          show: true,
          position: "right",
          distance: 6,
          color: textPrimary,
          fontSize: 11,
          formatter: (p: { value: number }) => p.value.toLocaleString(),
        },
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts
        option={option}
        style={{ width: "100%", height: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Box>
  );
};

export default CancerTypeBar;
